import React, {useState} from "react";
import {Link, Link as RouterLink} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Delete, Edit} from "@material-ui/icons";
import styled from "styled-components";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../util/ApiHelper";
import {
    CustomCheckbox,
    formatDatetime,
    modal
} from "../../../util";
import {useLoading} from "../../../util/LoadingProvider";
import {toast} from "react-toastify";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import {
    DragDropContext, Draggable,
    Droppable
} from "react-beautiful-dnd";
// to delete
import axios from 'axios';
import PaginationBar from "../../common/PaginationBar";

const useStyles = makeStyles((theme) => ({
    textInput: {
        backgroundColor: "white",
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    inactive: {
        color: "#999999",
    },
    buttonGroup: {
        marginTop: "20px",
        textAlign: "right",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
        background: "#ffffff",
        paddingTop: "10px",
        paddingBottom: "10px",
        minHeight: "800px",
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    button: {
        backgroundColor: "#306F9D",
        textTransform: "capitalize",
        color: "white",
        "&:hover": {
            backgroundColor: "#306F9D",
        },
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

/** DRAG N DROP PREPARATION STARTS **/

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

class DraggableTableCell extends React.Component {
    getSnapshotBeforeUpdate(prevProps) {
        if (!this.ref) {
            return null;
        }
        const isDragStarting =
            this.props.isDragOccurring && !prevProps.isDragOccurring;
        if (!isDragStarting) {
            return null;
        }
        const { width, height } = this.ref.getBoundingClientRect();
        const snapshot = {
            width,
            height,
        };
        return snapshot;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const ref = this.ref;
        if (!ref) {
            return;
        }
        if (snapshot) {
            if (ref.style.width === snapshot.width) {
                return;
            }
            ref.style.width = `${snapshot.width}px`;
            ref.style.height = `${snapshot.height}px`;
            return;
        }
        if (this.props.isDragOccurring) {
            return;
        }
        // inline styles not applied
        if (ref.style.width == null) {
            return;
        }
        // no snapshot and drag is finished - clear the inline styles
        ref.style.removeProperty("height");
        ref.style.removeProperty("width");
    }

    setRef = (ref) => {
        this.ref = ref;
    };

    render() {
        return (
            <TableCell
                style={{ paddingTop: 6, paddingBottom: 6 }}
                ref={this.setRef}
                align={this.props.align}
            >
                {this.props.children}
            </TableCell>
        );
    }
}

const DraggableTableRow = ({
                               snapshot,
                               data,
                               row,
                               provided,
                               className,
                           }) => {
    return (
        <IsDraggingContext.Consumer>
            {(isDragging) => (
                <TableRow
                    className={className}
                    ref={provided.innerRef}
                    isDragging={snapshot.isDragging}
                    {...provided.draggableProps}
                >
                    {row.cells.map((cell) => {
                        if (cell.column.id !== "dragHandle") {
                            return (
                                <DraggableTableCell isDragOccurring={isDragging} scope="row">
                                    {cell.render("Cell")}
                                </DraggableTableCell>
                            );
                        } else {
                            return null;
                        }
                    })}
                    <TableCell
                        style={{ paddingTop: 6, paddingBottom: 6, color: "#CCCCCC" }}
                        {...provided.dragHandleProps}
                    >
                        <DragHandleIcon />
                    </TableCell>
                </TableRow>
            )}
        </IsDraggingContext.Consumer>
    );
};

/** DRAG N DROP PREPARATION ENDS **/

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);


function SupportUsListingPage(props) {
    const {match, disabled} = props;
    const classes = useStyles();
    const [actionType, setActionType] = React.useState("");
    const [keyword, setKeyword] = React.useState("");
    const [queryString, setQueryString] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const [selectedDate, handleDateChange] = React.useState(null);
    const {setLoading} = useLoading();
    // for pagination
    const [page, setPage] = React.useState(0);
    const [allCounts, setAllCounts] = React.useState(0);
    const [inactiveCounts, setInactiveCounts] = React.useState(0);

    // for Drag & Drop
    const [orderingDisabled, setOrderingDisabled] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const onBeforeDragStart = () => {
        setIsDragging(true);
    };
    const onDragEnd = (result) => { //callback passed to dragdrop context
        // dropped outside the list
        if (!result.destination) {
            return;
        }
        const items = reorder(data, result.source.index, result.destination.index);
        console.log('ON DRAG END:', items);
        setData(items);
    };

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const handleSelectChange = (e) => {
        setActionType(e.target.value);
    };

    const deleteSupportUs = async (id) => {
        const decision = await modal.fire({
            title: "Delete support us",
            text: "Are you sure to delete this support us?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.hkiff.deleteSupportUsById(id);
                if (result.data.code === 0) {
                    toast.dark("SupportUs deleted.");
                    getSupportUs();
                    getInactiveSupportUs();
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Page title (English)",
                accessor: "pageTitleEn",
                Cell: ({row}) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.pageTitleEn}
                        </Link>
                      );
                },
            },
            {
                Header: "Page title (Traditional Chinese)",
                accessor: "pageTitleTc",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.pageTitleTc}
                        </Link>
                    );
                },
            },
            {
                Header: "Last Update",
                accessor: "updateTime",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return row.original.updateTime ? formatDatetime(row.original.updateTime) : '-';
                },
            },
        ],
        [totalPages]
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        selectedFlatRows,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            manualPagination: true,
            autoResetPage: false,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex"}}>
                            <IconButton
                                className={classes.iconBtn}
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}`}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            {!disabled && (
                                <IconButton
                                    className={classes.iconBtn}
                                    size="small"
                                    onClick={() => {
                                        deleteSupportUs(row.original.id);
                                    }}
                                >
                                    <Delete
                                        fontSize="inherit"/>
                                </IconButton>
                            )}
                        </div>
                    ),
                },
                // The drag handle can be used to drag the table's rows individually
                {
                    id: "dragHandle",
                    Header: () => <></>,
                },
            ]);
        }
    );

    const getSupportUs = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getSupportUsListing({
                page: pageIndex,
                pageSize,
                keyword: queryString,
            });
            console.log('RESULT:', result)
            setData(result.data.content);
            setAllCounts(result.data.totalElements);
            setTotalPages(result.data.totalPages);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [setLoading, pageIndex, pageSize, queryString]);

    const getInactiveSupportUs = React.useCallback(async() => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getSupportUsListing({
                keyword: queryString
            });
            setInactiveCounts(result.data.totalElements);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [setLoading, queryString])

    const triggerSearch = React.useCallback(() => {
        setQueryString(keyword);
    }, [gotoPage, keyword, setLoading]);

    const handleBulkAction = React.useCallback(async () => {
        switch (actionType) {
            case "DELETE":
                const ids = selectedFlatRows.map((o) => o.original.id);
                if (ids.length > 0) {
                    const decision = await modal.fire({
                        title: "Delete SupportUss",
                        text: `Are you sure to delete ${ids.length} support us(s)?`,
                        showCancelButton: true,
                        confirmButtonText: `Delete`,
                    });
                    if (decision.isConfirmed) {
                        try {
                            const promises = ids.map((id) =>
                                apiHelper.hkiff.deleteSupportUsById(id)
                            );
                            await Promise.all(promises);
                            toast.dark(`${ids.length} page(s) deleted.`);
                            await getSupportUs();
                        } catch (err) {
                            console.log(err);
                        }
                    }
                }
                break;
            default:
                break;
        }
    }, [actionType, selectedFlatRows, getSupportUs]);

    const saveOrdering = async () => {
        try {
            const seqData = {};
            data.forEach((e, i) => {
                seqData[e.id] = 1 + i;
            });
            console.log(seqData);
            const result = await apiHelper.hkiff.orderSupportUsListing(seqData);
            if (result.data.code === 0) {
                toast.dark("Your changes is saved.");
                await getSupportUs();
            }
        } catch (err) {
            console.log(err);
        }
    };

    // disable ordering if used search
    React.useEffect(() => {
        if (queryString) {
            setOrderingDisabled(true);
        } else {
            setOrderingDisabled(false);
        }
    }, [queryString])

    React.useEffect(() => {
        getSupportUs();
        getInactiveSupportUs();
    }, [getSupportUs, getInactiveSupportUs]);

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                Support Us
            </Typography>
            <form>
                
                
                <Grid
                    container
                    justify="space-between"
                    style={{paddingBottom: "10px"}}
                >
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            alignItems="center"
                        >
                            <Box flex>
                                <FormControl fullWidth
                                             margin="dense"
                                             variant="outlined">
                                    <InputLabel
                                        id="demo-simple-select-outlined-label">
                                        Bulk Actions
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-outlined-label"
                                        id="demo-simple-select-outlined"
                                        value={actionType}
                                        onChange={handleSelectChange}
                                        label="Bulk Actions"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem
                                            value={"DELETE"}>Delete</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box flex>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleBulkAction}
                                    disabled={disabled}
                                >
                                    Apply
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={RouterLink}
                                    disabled={disabled}
                                    to={`${match.url}/new`}
                                >
                                    Add New SupportUs
                                </Button>
                            </Box>
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <IsDraggingContext.Provider value={isDragging}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                                  className="fieldset">
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{margin: "0"}}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{width: "5%"}}/>
                                    <col
                                        style={{width: "20%"}}/>
                                    <col
                                        style={{width: "20%"}}/>
                                    <col
                                        style={{width: "15%"}}/>
                                    <col
                                        style={{width: "5%"}}/>
                                    <col
                                        style={{width: "5%"}}/>
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <DragDropContext
                                    onBeforeDragStart={onBeforeDragStart}
                                    onDragEnd={onDragEnd}
                                >
                                    <Droppable droppableId="droppable">
                                        {(provided, snapshot) => (
                                <TableBody
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {rows.map((row, index) => {
                                        prepareRow(row);
                                        return (
                                            <Draggable
                                                key={row.id}
                                                draggableId={row.id}
                                                index={index}
                                                isDragDisabled={disabled || orderingDisabled}
                                            >
                                                {(provided, snapshot) => (
                                                    <DraggableTableRow
                                                        key={row.id}
                                                        data={row.original}
                                                        row={row}
                                                        provided={provided}
                                                        snapshot={snapshot}
                                                    />
                                                    )}
                                            </Draggable>
                                        );
                                    })}
                                </TableBody>
                                            )}
                                    </Droppable>
                                </DragDropContext>
                            </Table>
                        </fieldset>
                    </TableContainer>
                    </IsDraggingContext.Provider>
                </Grid>
                <Grid
                    container
                    justify="space-between"
                >
                <Grid
                    container
                    item
                    xs={4}
                >
                    <Box display="flex">
                        <Button
                            variant="contained"
                            color="primary"
                            type="button"
                            disabled={disabled}
                            onClick={saveOrdering}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </Grid>
                <Grid
                    container
                    item
                    xs={8}
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div className={classes.flexRow}>
                        <div></div>
                        <PaginationBar
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            gotoPage={gotoPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageIndex={pageIndex}
                            totalCount={allCounts}
                        />
                    </div>
                </Grid>
                </Grid>
            </form>
        </div>
    );
}

export default SupportUsListingPage;
